import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { getOptimizedVideoUrl, getOptimizedImageUrl } from '../../lib/optimizedAssets';

interface VideoDemoProps {
  isDark: boolean;
  isMobile: boolean;
}

export function VideoDemo({ isDark, isMobile }: VideoDemoProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reload video when theme or device type changes
  useEffect(() => {
    if (videoRef.current) {
      const wasPlaying = isPlaying;
      const currentTime = videoRef.current.currentTime;
      
      // Pause the video and reset playing state
      videoRef.current.pause();
      setIsPlaying(false);
      
      // Load the new video source
      videoRef.current.load();
      
      // If video was playing before, restart it after a short delay
      if (wasPlaying) {
        const timeoutId = setTimeout(async () => {
          if (videoRef.current) {
            try {
              videoRef.current.currentTime = currentTime;
              await videoRef.current.play();
              setIsPlaying(true);
            } catch (error) {
              console.error('Error restarting video after theme/device change:', error);
            }
          }
        }, 100);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [isDark, isMobile]); // Trigger when theme or device type changes

  const togglePlay = async () => {
    if (videoRef.current) {
      try {
        if (isPlaying) {
          await videoRef.current.pause();
        } else {
          await videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
      } catch (error) {
        console.error('Error toggling video playback:', error);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = async () => {
    if (videoRef.current) {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        } else {
          await videoRef.current.requestFullscreen();
        }
      } catch (error) {
        console.error('Error toggling fullscreen:', error);
      }
    }
  };

  // Handle video end
  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  // Determine which poster image to use based on theme and device
  const getPosterImage = () => {
    if (isMobile) {
      return isDark
        ? getOptimizedImageUrl('mocktrail-hero-mobile-darkui.png')
        : getOptimizedImageUrl('mocktrail-hero-mobile-lightui.png');
    } else {
      return isDark
        ? getOptimizedImageUrl('mocktrail-hero-darkui.png')
        : getOptimizedImageUrl('mocktrail-hero-lightui.png');
    }
  };

  // Get optimized video URL
  const getVideoUrl = () => {
    if (isMobile) {
      const videoUrl = isDark ? getOptimizedVideoUrl('hero-demo-mobile-dark') : getOptimizedVideoUrl('hero-demo-mobile-light');
      return videoUrl;
    } else {
      const videoUrl = isDark ? getOptimizedVideoUrl('hero-demo-dark') : getOptimizedVideoUrl('hero-demo-light');
      return videoUrl;
    }
  };

  return (
    <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-primary-light/10 via-secondary-light/10 to-primary-light/10 dark:from-primary-dark/10 dark:via-secondary-dark/10 dark:to-primary-dark/10 p-1.5 shadow-lg">
      <div className="relative rounded-xl overflow-hidden aspect-[8/16] sm:aspect-[12/7] bg-surface-light dark:bg-surface-dark">
        <video
          ref={videoRef}
          className="w-full h-full object-cover md:object-cover bg-background-light dark:bg-background-dark"
          poster={getPosterImage()}
          onEnded={handleVideoEnd}
        >
          <source src={getVideoUrl()} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Video Controls */}
        <div className={`absolute inset-0 flex items-center justify-center ${isPlaying ? 'opacity-0' : 'opacity-100'} md:opacity-0 hover:opacity-100 transition-opacity duration-300 bg-gradient-to-b from-black/20 to-black/60`}>
          <button
            onClick={togglePlay}
            className="w-20 h-20 flex items-center justify-center rounded-full bg-white/20 dark:bg-black/20 backdrop-blur-sm hover:bg-white/30 dark:hover:bg-black/30 transition-all duration-300 hover:scale-110"
          >
            {isPlaying ? (
              <Pause className="w-10 h-10 text-white" />
            ) : (
              <Play className="w-10 h-10 text-white translate-x-0.5" />
            )}
          </button>
        </div>

        {/* Top Right Controls */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          <button
            onClick={toggleMute}
            className="p-2.5 rounded-lg bg-black/20 dark:bg-white/20 backdrop-blur-sm hover:bg-black/30 dark:hover:bg-white/30 transition-all duration-300 hover:scale-110"
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5 text-white" />
            ) : (
              <Volume2 className="w-5 h-5 text-white" />
            )}
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2.5 rounded-lg bg-black/20 dark:bg-white/20 backdrop-blur-sm hover:bg-black/30 dark:hover:bg-white/30 transition-all duration-300 hover:scale-110"
          >
            <Maximize className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}